import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardService, DashboardData } from '../services/dashboardService';
import CostSummary from '../components/CostSummary';
import Charts from '../components/Charts';
import PdfExport from '../components/PdfExport';
import PermissionGuard from '../components/PermissionGuard';
import AccessDenied from '../components/AccessDenied';
import WorkTypeBadge from '../components/WorkTypeBadge';
import { generateMonths } from '../utils/monthUtils';
import { workTypeLabel } from '../utils/workScope';
import opportunityService from '../services/opportunityService';

/** Saved figures plus what they were saved for - a project or an opportunity. */
type SavedView = Omit<DashboardData, 'projectId' | 'projectName'> & {
  workId: number;
  workName?: string | null;
};

interface ViewSavedDashboardPageProps {
  /** Which store the :id belongs to - saved project dashboards or opportunity scenarios. */
  kind?: 'project' | 'opportunity';
}

const ViewSavedDashboardPage: React.FC<ViewSavedDashboardPageProps> = ({ kind = 'project' }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<SavedView | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const exportRef = useRef<HTMLElement>(null!);

  useEffect(() => {
    if (id) {
      loadDashboardData(parseInt(id));
    }
  }, [id, kind]);

  const loadDashboardData = async (dashboardId: number) => {
    setLoading(true);
    setError(null);

    try {
      if (kind === 'opportunity') {
        const scenario = await opportunityService.getScenario(dashboardId);
        setDashboardData({
          ...scenario,
          ownerId: scenario.ownerId ?? undefined,
          workId: scenario.opportunityId,
          workName: scenario.opportunityName,
        });
      } else {
        const data = await DashboardService.getDashboardById(dashboardId);
        if (!data) {
          throw new Error('Dashboard not found');
        }
        const { projectId, projectName, ...figures } = data;
        setDashboardData({ ...figures, workId: projectId, workName: projectName });
      }
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (error || !dashboardData) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Dashboard not found'}</p>
          <button
            onClick={() => navigate('/saved-dashboards')}
            className="mt-4 text-red-600 hover:text-red-800 underline"
          >
            Back to Saved Project Analysis
          </button>
        </div>
      );
    }

    // Generate months for display
    const months = generateMonths(new Date().toISOString().split('T')[0], 12);

    const viewingOpportunity = kind === 'opportunity';

    return (
      <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/saved-dashboards')}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Saved {workTypeLabel(viewingOpportunity)} {viewingOpportunity ? 'Scenario' : 'Dashboard'}
                  </h2>
                  <WorkTypeBadge isOpportunity={viewingOpportunity} />
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  {dashboardData.workName ? `${dashboardData.workName} • ` : ''}
                  {workTypeLabel(viewingOpportunity)} ID: {dashboardData.workId} • Saved on {formatDate(dashboardData.savedAt)}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(viewingOpportunity ? `/whatif?opportunityScenario=${id}` : `/whatif?edit=${id}`)}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
            >
              {viewingOpportunity ? 'Edit Scenario' : 'Edit Dashboard'}
            </button>
            {dashboardData && (
              <PdfExport
                targetRef={exportRef}
                filename={`${viewingOpportunity ? 'opportunity-scenario' : 'project-dashboard'}-${dashboardData.workId}-${new Date().toISOString().split('T')[0]}.pdf`}
              />
            )}
          </div>
        </div>
      </div>

      {/* Exportable Content Wrapper */}
      <div ref={exportRef as React.RefObject<HTMLDivElement>} className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Total Cost</div>
          <div className="mt-2 text-2xl font-bold text-gray-900">
            {formatCurrency(dashboardData.calculatedCosts.totalCost)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Employee Costs</div>
          <div className="mt-2 text-2xl font-bold text-blue-600">
            {formatCurrency(dashboardData.calculatedCosts.totalEmployeeCosts)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Billable Costs</div>
          <div className="mt-2 text-2xl font-bold text-green-600">
            {formatCurrency(dashboardData.calculatedCosts.billableCosts)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Non-Billable Costs</div>
          <div className="mt-2 text-2xl font-bold text-orange-600">
            {formatCurrency(dashboardData.calculatedCosts.nonBillableCosts)}
          </div>
        </div>
      </div>

      {/* Team Allocation Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Team Allocation</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team Member</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hourly Rate for Client</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Hours</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dashboardData.allocationMembers.map((member) => {
                const totalHours = Object.values(member.monthlyAllocations).reduce((sum, hours) => sum + hours, 0);
                // Costed at the client rate, as the allocation grid does
                const clientRate = member.clientHourlyRate ?? member.member.per_hour_cost ?? 0;
                const totalCost = totalHours * clientRate;

                return (
                  <tr key={member.id}>
                    {/* Name, email, role and hourly rate share one column, as in the grid */}
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {member.member.firstname} {member.member.lastname}
                      </div>
                      <div className="text-xs text-gray-500">{member.member.login}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs">
                        <span className="text-gray-600">{member.member.role_name || 'N/A'}</span>
                        <span className="text-gray-300">|</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(member.member.per_hour_cost || 0)}/hr
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(clientRate)}/hr
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {totalHours.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                      {formatCurrency(totalCost)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Project Costs */}
      {dashboardData.projectCosts.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Project Costs</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Billable</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dashboardData.projectCosts.map((cost) => {
                  const costTotal = Object.values(cost.monthlyCosts || {}).reduce((sum, val) => sum + (val || 0), 0);
                  return (
                    <tr key={cost.id}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {cost.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {cost.billable ? (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Yes</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">No</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {formatCurrency(costTotal)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cost Summary and Charts */}
      <CostSummary
        allocationMembers={dashboardData.allocationMembers}
        months={months}
        projectCosts={dashboardData.projectCosts}
        endDate=""
        profitPercent={dashboardData.calculatedCosts?.profitPercent ?? undefined}
        revenue={dashboardData.calculatedCosts?.revenue ?? undefined}
      />

      <Charts
        allocationMembers={dashboardData.allocationMembers}
        months={months}
        projectCosts={dashboardData.projectCosts}
        endDate=""
      />
      </div>
      {/* End Exportable Content */}
    </div>
    );
  };

  return (
    <PermissionGuard
      pbsRole="project_owner"
      fallback={
        <AccessDenied
          title="Project Owner Access Required"
          message="Saved dashboards are only available to PBS-approved project owners."
        />
      }
    >
      {renderContent()}
    </PermissionGuard>
  );
};

export default ViewSavedDashboardPage;
